import { useEffect, useRef } from 'react'

interface UseCanvasEscapeOptions {
  /** Nur bei offenem Canvas im sichtbaren Tab (TabStack-Falle). */
  enabled: boolean
  /**
   * Das Canvas-Element, an dem gehorcht wird — NICHT das `window`.
   * `null`, solange React Flow seine Pane noch nicht aufgebaut hat.
   */
  container: HTMLElement | null
  onEscape: () => void
}

/**
 * Escape hebt die Auswahl auf dem Canvas auf.
 *
 * **Warum am Canvas-Element und nicht am `window`** — dieselbe Begründung wie
 * bei `useCanvasDuplicateShortcut`, und ebenso konkret:
 * `useProjectNavigation.ts:769` horcht am `window` und leert dort die Auswahl
 * der **Aufgabenliste**. Am `window` feuerten beide, und ein Escape im Canvas
 * räumte nebenbei die Liste ab.
 *
 * React Flows eigenes Escape reicht nicht: Es sitzt im `onKeyDown` eines
 * fokussierten Nodes und hebt dort genau diesen einen auf — die übrigen einer
 * Mehrfachauswahl blieben stehen.
 *
 * Das setzt voraus, dass der Fokus im Canvas liegt; darum holt ihn sich der
 * Auswahlrahmen beim Erscheinen (siehe `SelectionOverlay`).
 *
 * @example
 * useCanvasEscape({ enabled, container: canvasElement, onEscape: clearSelection })
 */
export function useCanvasEscape({ enabled, container, onEscape }: UseCanvasEscapeOptions) {
  const onEscapeRef = useRef(onEscape)
  onEscapeRef.current = onEscape

  useEffect(() => {
    if (!enabled || !container) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Im Bearbeiten-Modus gehört Escape dem Textfeld: Es schließt dort den
      // Text ab. Der Handler am Feld ruft `blur()` und stoppt das Ereignis
      // ohnehin, dies ist der Gürtel zum Hosenträger.
      const tag = (e.target as HTMLElement | null)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      // Einem offenen Menü seine Tasten lassen.
      if (document.querySelector('[role="menu"]')) return

      e.preventDefault()
      // Hält die Aufgabenliste ab (siehe oben).
      e.stopPropagation()
      onEscapeRef.current()
    }

    container.addEventListener('keydown', handleKeyDown)
    return () => container.removeEventListener('keydown', handleKeyDown)
  }, [enabled, container])
}
